import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Empty, Skeleton, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';

import httpClient from '../../utils/HttpClient';
import { PRODUCT_KNOWLEDGE_URL } from '../../constants/Url';
import { objectToQuery, formatReadingTime } from '../../utils/Utils';
import CustomPagination from '../../components/common/CustomPagination';

export default function ProductKnowledgePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const initialKeyword = searchParams.get('keyword') || '';
  const initialCategory = searchParams.get('category') || 'ALL';
  const initialPage = parseInt(searchParams.get('pageNo') || '1', 10);

  const [keyword, setKeyword] = useState(initialKeyword);
  const [searchTerm, setSearchTerm] = useState(initialKeyword);
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(9);

  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Available categories
  const categories = [
    { key: 'ALL', name: t('category_all') || '全部' },
    { key: '技术展示', name: t('menu_tech_showcase') || '技术展示' },
    { key: '冷知识', name: t('menu_trivia') || '冷知识' },
  ];

  const fetchArticles = async (params = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        keyword: params.keyword !== undefined ? params.keyword : searchTerm,
        releaseYear: 'ALL',
        pageNo: params.pageNo || currentPage,
        pageSize: params.pageSize || pageSize,
      };

      const cat = params.category !== undefined ? params.category : activeCategory;
      if (cat && cat !== 'ALL') {
        queryParams.category = cat;
      }

      const res = await httpClient
        .get(objectToQuery(PRODUCT_KNOWLEDGE_URL, queryParams))
        .then((r) => r.data)
        .catch((err) => ({ status: 500, message: err?.message }));

      if (res && res.status === 200 && Array.isArray(res.data)) {
        setArticles(res.data);
        setTotal(res.total || 0);
      } else {
        setArticles([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('fetchArticles error:', error);
      setArticles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search input handler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((nextValue) => {
      setSearchTerm(nextValue);
      setCurrentPage(1);
    }, 400),
    []
  );

  const handleKeywordChange = (e) => {
    const val = e.target.value;
    setKeyword(val);
    debouncedSearch(val);
  };

  const handleCategoryChange = (key) => {
    setActiveCategory(key);
    setCurrentPage(1);
  };

  useEffect(() => {
    fetchArticles({
      keyword: searchTerm,
      category: activeCategory,
      pageNo: currentPage,
      pageSize,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, activeCategory, currentPage, pageSize]);

  return (
    <div className="w-full min-h-screen pb-16 bg-[#f7f9fc]">
      {/* ── Top Header Banner Section ── */}
      <div className="w-full py-10 px-4 text-center bg-gradient-to-r from-[#4d9dec] via-[#637cfb] to-[#a380f9] text-white">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-wide m-0 text-white mb-2">
          {t('menu_product_knowledge') || 'Categories'}
        </h1>
        <p className="text-[13px] sm:text-[14px] opacity-90 m-0">
          {t('menu_product_knowledge_sub') || t('menu_tech_showcase_sub')}
        </p>
      </div>

      <div className="max-w-[1263px] mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        {/* Search Bar */}
        <div className="flex justify-end mb-8">
          <Input
            prefix={<SearchOutlined className="text-gray-400 mr-1" />}
            placeholder={t('text_search_placeholder')}
            value={keyword}
            onChange={handleKeywordChange}
            allowClear
            className="!w-full sm:!w-[40%] lg:!w-[30%] !rounded-full !py-1.5 !px-4 !shadow-sm"
          />
        </div>

        {/* 3-Column Exhibition Grid / Skeletons / Empty */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-gray-100 p-5 sm:p-6 flex flex-col gap-4 shadow-sm"
              >
                <div className="w-full aspect-[4/3] rounded-md bg-gray-50 flex items-center justify-center overflow-hidden">
                  <Skeleton.Image className="!w-full !h-full" active />
                </div>
                <Skeleton.Input active size="default" style={{ width: '80%' }} />
                <Skeleton active paragraph={{ rows: 2 }} title={false} className="mt-2" />
                <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                  <Skeleton.Input active size="small" style={{ width: '30%', height: '16px' }} />
                  <Skeleton.Input active size="small" style={{ width: '25%', height: '16px' }} />
                </div>
              </div>
            ))}
          </div>
        ) : articles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {articles.map((item, index) => (
              <div
                key={(item.productKnowledgeId || item._id || item.id) + '-' + index}
                className="bg-white rounded-lg overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col h-full"
              >
                {/* Image cover */}
                <div
                  className="cursor-pointer"
                  onClick={() =>
                    navigate(`/product-knowledge/${item.productKnowledgeId || item._id || item.id}`)
                  }
                >
                  <div className="w-full aspect-[4/3] overflow-hidden relative">
                    <img
                      src={
                        (typeof item.coverimage === 'object' && item.coverimage?.url) ? item.coverimage.url :
                        (item.coverimage || item.coverImage || item.image || item.imageUrl || item.imagePath || '')
                      }
                      alt={item.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                    {item.category && (
                      <span className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs px-2.5 py-1 rounded-full font-medium">
                        {t(item.category) || item.category}
                      </span>
                    )}
                  </div>

                  {/* Content block */}
                  <div className="p-5 sm:p-6 flex flex-col flex-1">
                    <h3 className="text-[15px] sm:text-base font-bold text-gray-900 leading-snug mb-3 line-clamp-1 cursor-pointer hover:text-[#637cfb] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-xs sm:text-[13px] leading-relaxed line-clamp-3 mb-6 flex-1">
                      {item?.subtitle}
                    </p>

                    {/* Footer details */}
                    <div className="flex justify-between items-center pt-4 border-t border-gray-50 mt-auto">
                      <span className="text-[12px] text-gray-400">
                        {t('text_read_time') || '阅读时长'}: {item?.readingTime ? `${item.readingTime} ${t('text_minutes') || '分钟'}` : formatReadingTime(item?.readingTime)}
                      </span>
                      <span
                        onClick={() =>
                          navigate(
                            `/product-knowledge/${item.productKnowledgeId || item._id || item.id}`
                          )
                        }
                        className="text-[12px] gradient-text-button font-bold flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        {t('text_in_depth') || '深入研习'} &rarr;
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('text_no_content') || '暂无内容'}
          />
        )}

        {/* ── Pagination ── */}
        {(loading || total > 0 || articles.length > 0) && (
          <div className="flex justify-end mt-12">
            <CustomPagination
              current={currentPage}
              pageSize={pageSize}
              total={loading ? 9 : total || articles.length || 0}
              pageSizeOptions={[9, 18, 27, 36, 45]}
              onChange={(page, limit) => {
                if (limit !== pageSize) {
                  setPageSize(limit);
                  setCurrentPage(1);
                } else {
                  setCurrentPage(page);
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
